import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { STOCK_OWNERSHIPS } from '@lensmira/domain'

import type { SupplierRow } from '../api/reads.js'
import { Ltr, useTranslation } from '../i18n.js'
import { MoneyInputError, parseMajorInput } from '../money.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { ownershipLabel } from '../status-labels.js'

/**
 * Raising a purchase order. §6.29 `SUP-001`, `SUP-002`, §15.6 step 2, Appendix C.5.
 *
 * `Purchasing.tsx` is the counter's half of this module: it lists what is already on order, moves it
 * along C.5, and books a delivery in. Nothing in the product could **start** an order, so the whole
 * of `POST /purchase-orders` was unreachable from the app and the only orders a shop could have were
 * the ones `pnpm seed` put there. This is that endpoint, and it is the accountant's job rather than
 * the counter's: `purchase_order_raise` is owner, manager and accountant, and `stock_receive`, the
 * row the delivery panel takes, is owner, manager and dispenser.
 *
 * **The order lands in `draft`, and this screen stops there.** C.5 makes `draft -> sent` a separate
 * move with its own transmission channel (`SUP-003`), and `Purchasing.tsx` already offers it. A
 * screen that raised and sent in one tap would take away the one chance anybody has to read the
 * order back before a wholesaler sees it, and `SUP-001`'s point is that the order leaves the product
 * rather than a notebook.
 *
 * **The supplier is typed, because nothing can list suppliers.** There is no `GET /suppliers`:
 * `supplier` and `supplier_price` are real tables with no read route over them, so the screen cannot
 * offer a name to pick and asks for the id. The server answers `SUPPLIER_NOT_FOUND` (404) or
 * `SUPPLIER_INACTIVE` (409) for a wrong one, which is a refusal this screen cannot pre-empt and must
 * not pretend to, so the field says what it wants and the hint says why it is an id.
 *
 * **The currency is the branch's by default, and is a decision rather than a silent one.** The
 * service defaults an omitted currency to the supplier's own invoicing currency, which this screen
 * cannot read for the reason above, and an amount cannot be turned into minor units without knowing
 * the currency's decimal places: `parseMajorInput` reads `MINOR_DECIMALS`, so "500" is 50000 in PKR
 * and 500 in JPY. So the field is always sent and always filled, and a line taking the supplier's
 * list price in another currency is refused with `SUPPLIER_PRICE_CURRENCY`, which the hint names.
 *
 * **A line names a cost or asks for the supplier's, never both.** `COST_AMBIGUOUS` is a certain 422
 * and a refused write stops the whole queue (`OFF-005`), so the cost field is not merely ignored
 * when the price-list box is ticked: it is not rendered, and nothing can be sent from a field that
 * is not on screen.
 *
 * **`on_loan` is not offered.** `INV-003` has three ownerships and the service refuses all but
 * `owned` and `consignment` with `OWNERSHIP_INVALID`; a frame on loan arrives from a rep and is
 * never bought, so there is nothing to raise an order for. Filtered out of the domain's own list
 * rather than copied into a shorter one here, the same way `Promotions.tsx` drops `category` from
 * `APPLIES_TO`, so the day Appendix E gains a fourth ownership this offers it.
 *
 * **The catalogue reference is asked for and explained.** A line with no `productId` is accepted,
 * and then the goods receipt cannot post stock for it: `ReceiptStock` records
 * `posted: false, reason: 'NO_CATALOGUE_REF'`, which is a delivery that arrives, is signed for, and
 * never reaches a shelf. That is a consequence of a blank field on this screen that nobody standing
 * at the dock a week later can repair, so it is said here rather than discovered there.
 */

export interface RaisePurchaseOrderProps {
  /** `BR-08`: the order is raised at this branch, and the server checks the caller is there. */
  readonly branchId: string
  /**
   * The branch's own currency, as the default for a new order. From the caller rather than invented
   * here: every amount on this screen is parsed at this currency's decimal places, and a guess
   * would be an order priced at a hundred times what somebody typed.
   */
  readonly currency: string
  /**
   * The shop's suppliers, for the chooser.
   *
   * Absent means the shell has no reads config, which is the same shape `ShopSetup` uses for its
   * staff list. Without it this screen asked for a UUID.
   */
  readonly loadSuppliers?: () => Promise<readonly SupplierRow[]>
}

/** One line being written. All text, because what a shopkeeper typed is text until it is checked. */
interface LineDraft {
  readonly sku: string
  readonly description: string
  readonly orderedQuantity: string
  readonly unitCost: string
  /** `SUP-002`: take the price in force from the supplier's own list instead of typing one. */
  readonly fromPriceList: boolean
  readonly productId: string
  readonly ownership: string
}

const EMPTY_LINE: LineDraft = {
  sku: '',
  description: '',
  orderedQuantity: '',
  unitCost: '',
  fromPriceList: false,
  productId: '',
  // `INV-003`: the service's own default, stated here so the form and the server agree about what
  // an untouched line means.
  ownership: 'owned',
}

/** The order number off the raise response, or undefined when the answer is not that shape. */
function numberOf(response: unknown): string | undefined {
  if (typeof response !== 'object' || response === null) return undefined
  const value = (response as { number?: unknown }).number
  return typeof value === 'string' ? value : undefined
}

export function RaisePurchaseOrder({
  branchId,
  currency,
  loadSuppliers,
}: RaisePurchaseOrderProps): JSX.Element {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [supplierId, setSupplierId] = useState('')
  const [suppliers, setSuppliers] = useState<readonly SupplierRow[] | null>(null)
  const [suppliersFailed, setSuppliersFailed] = useState(false)

  useEffect(() => {
    if (loadSuppliers === undefined) return
    loadSuppliers().then(
      (rows) => {
        setSuppliers(rows)
        setSuppliersFailed(false)
      },
      () => {
        // Its own state, not an empty list: "this shop has no suppliers" is a different sentence
        // from "the list could not be read", and only one of them is ever true at a time.
        setSuppliers(null)
        setSuppliersFailed(true)
      },
    )
  }, [loadSuppliers])
  const [orderCurrency, setOrderCurrency] = useState(currency)
  const [expectedDeliveryOn, setExpectedDeliveryOn] = useState('')
  const [lines, setLines] = useState<readonly LineDraft[]>([EMPTY_LINE])
  const [raised, setRaised] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [writeId, setWriteId] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, (write) => {
    setWriteId(null)
    // The number is what the accountant reads down the phone to the wholesaler, and it exists only
    // in the response: `TAX-002`'s reasoning applies to a purchase order too, the id is allocated
    // by the server. Kept on screen while the form is cleared, so clearing the form does not lose
    // the one thing the write produced.
    setRaised(numberOf(write.response) ?? '')
    setSupplierId('')
    setExpectedDeliveryOn('')
    setLines([EMPTY_LINE])
    setOrderCurrency(currency)
  })

  const change = (index: number, patch: Partial<LineDraft>): void => {
    setLines((previous) => previous.map((line, i) => (i === index ? { ...line, ...patch } : line)))
  }

  const raise = useCallback(async () => {
    if (supplierId.trim() === '') {
      setError(t('raiseOrder.supplierRequired'))
      return
    }
    if (orderCurrency.trim() === '') {
      // Not omitted so the server can default it: there is no supplier read, so an order whose
      // currency this screen does not know is an order whose costs it cannot parse.
      setError(t('raiseOrder.currencyRequired'))
      return
    }
    // A line nobody filled in at all is dropped rather than refused: the form opens with one blank
    // row and ends with one, and an accountant who added a row and changed their mind should not
    // have to remove it to save.
    const filled = lines.filter(
      (line) =>
        line.sku.trim() !== '' ||
        line.description.trim() !== '' ||
        line.orderedQuantity.trim() !== '' ||
        line.unitCost.trim() !== '',
    )
    if (filled.length === 0) {
      setError(t('raiseOrder.linesRequired'))
      return
    }

    const body: Record<string, unknown>[] = []
    for (const line of filled) {
      if (line.sku.trim() === '' || line.description.trim() === '') {
        setError(t('raiseOrder.skuAndDescriptionRequired'))
        return
      }
      const quantity = Number(line.orderedQuantity.trim())
      if (!Number.isSafeInteger(quantity) || quantity < 1) {
        // `asPieces`: a whole number of pieces, one or more. A line for nothing is not an order.
        setError(t('raiseOrder.quantityWhole'))
        return
      }
      let unitCostMinor: string | undefined
      if (!line.fromPriceList) {
        try {
          unitCostMinor = parseMajorInput(line.unitCost, orderCurrency.trim()).toString()
        } catch (inputError) {
          setError(
            inputError instanceof MoneyInputError
              ? inputError.message
              : t('raiseOrder.costInvalid'),
          )
          return
        }
      }
      body.push({
        sku: line.sku.trim(),
        description: line.description.trim(),
        orderedQuantity: quantity,
        ownership: line.ownership,
        // Exactly one of the two, because the service refuses both together with `COST_AMBIGUOUS`
        // and refuses neither with `FIELD_REQUIRED`.
        ...(line.fromPriceList ? { fromPriceList: true } : { unitCostMinor }),
        ...(line.productId.trim() === '' ? {} : { productId: line.productId.trim() }),
      })
    }

    setError(null)
    setRaised(null)
    const write = (await enqueue({
      method: 'POST',
      path: '/purchase-orders',
      label: t('raiseOrder.queueLabel'),
      // No `expectedVersion`: nothing exists yet to be stale against. `ARC-006`'s idempotency key
      // is what stops a double tap raising two orders, and the queue supplies it.
      body: {
        supplierId: supplierId.trim(),
        branchId,
        currency: orderCurrency.trim(),
        ...(expectedDeliveryOn === '' ? {} : { expectedDeliveryOn }),
        lines: body,
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [branchId, enqueue, expectedDeliveryOn, lines, orderCurrency, supplierId, t])

  const line = statusLine(t, snapshot, writeId, t('queue.subject.purchaseOrder'))

  return (
    <section className="lm-screen" aria-labelledby="raise-order-heading">
      <h1 id="raise-order-heading" className="lm-screen__heading">
        {t('raiseOrder.heading')}
      </h1>

      {error === null ? null : (
        <p className="lm-hint" role="alert">
          {error}
        </p>
      )}

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      {raised === null ? null : raised === '' ? (
        // Accepted with an answer this screen cannot read is still accepted, so it says that much
        // rather than replacing a confirmation with nothing.
        <p className="lm-hint">{t('raiseOrder.raisedUnnumbered')}</p>
      ) : (
        <p className="lm-hint">
          {sentence(t, 'raiseOrder.raised', { number: <Ltr>{raised}</Ltr> })}
        </p>
      )}

      {/*
        A chooser, which this could not be until `GET /suppliers` existed.
        It asked for the supplier's UUID and apologised for it in a hint underneath, in a product
        where five separate defects this session were a UUID merely being shown.

        Only the suppliers still in use: ordering from one the shop has stopped using is not a
        thing a chooser should offer, and the list carries the flag so this can tell.
      */}
      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('raiseOrder.supplierLabel')}</span>
        <select
          className="lm-input"
          value={supplierId}
          onChange={(e) => {
            setSupplierId(e.currentTarget.value)
          }}
        >
          <option value="">{t('raiseOrder.supplierUnchosen')}</option>
          {(suppliers ?? [])
            .filter((supplier) => supplier.isActive)
            .map((supplier) => (
              <option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </option>
            ))}
        </select>
      </label>
      {suppliersFailed ? <p className="lm-hint">{t('raiseOrder.suppliersFailed')}</p> : null}
      {suppliers !== null && suppliers.filter((s) => s.isActive).length === 0 ? (
        <p className="lm-hint">{t('raiseOrder.noSuppliers')}</p>
      ) : null}

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('raiseOrder.currencyLabel')}</span>
        <input
          className="lm-input"
          autoComplete="off"
          value={orderCurrency}
          onChange={(e) => {
            setOrderCurrency(e.currentTarget.value)
          }}
        />
      </label>
      {/* Every cost below is read at this currency's decimal places, and a price-list line priced
          in another one is refused by the server rather than converted. */}
      <p className="lm-hint">{t('raiseOrder.currencyHint')}</p>

      <label className="lm-field lm-field--inline">
        <span className="lm-field__label">{t('raiseOrder.expectedLabel')}</span>
        <input
          className="lm-input"
          type="date"
          value={expectedDeliveryOn}
          onChange={(e) => {
            setExpectedDeliveryOn(e.currentTarget.value)
          }}
        />
      </label>
      {/* `SUP-005` compares this with the server's own clock to call an order overdue, so an order
          with no date promised is an order nobody can chase. */}
      <p className="lm-hint">{t('raiseOrder.expectedHint')}</p>

      <ul className="lm-po-draft-list">
        {lines.map((draft, index) => (
          // The index is the key because a draft line has no id until the server gives the order
          // one, and the rows are only ever appended to or cleared together.
          <li key={index} className="lm-po-draft">
            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('raiseOrder.skuLabel', { position: index + 1 })}
              </span>
              <input
                className="lm-input"
                autoComplete="off"
                value={draft.sku}
                onChange={(e) => {
                  change(index, { sku: e.currentTarget.value })
                }}
              />
            </label>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('raiseOrder.descriptionLabel', { position: index + 1 })}
              </span>
              <input
                className="lm-input"
                autoComplete="off"
                value={draft.description}
                onChange={(e) => {
                  change(index, { description: e.currentTarget.value })
                }}
              />
            </label>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('raiseOrder.quantityLabel', { position: index + 1 })}
              </span>
              <input
                className="lm-input"
                inputMode="numeric"
                autoComplete="off"
                value={draft.orderedQuantity}
                onChange={(e) => {
                  change(index, { orderedQuantity: e.currentTarget.value })
                }}
              />
            </label>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('raiseOrder.fromPriceListLabel', { position: index + 1 })}
              </span>
              <input
                type="checkbox"
                checked={draft.fromPriceList}
                onChange={(e) => {
                  change(index, { fromPriceList: e.currentTarget.checked })
                }}
              />
            </label>

            {draft.fromPriceList ? (
              // `SUP-002`: the price in force on the day, from the supplier's list. Said rather
              // than left as an empty space where the cost field was.
              <p className="lm-hint">{t('raiseOrder.priceListMeans')}</p>
            ) : (
              <label className="lm-field lm-field--inline">
                <span className="lm-field__label">
                  {t('raiseOrder.costLabel', {
                    position: index + 1,
                    currency: orderCurrency.trim(),
                  })}
                </span>
                <input
                  className="lm-input"
                  inputMode="decimal"
                  autoComplete="off"
                  value={draft.unitCost}
                  onChange={(e) => {
                    change(index, { unitCost: e.currentTarget.value })
                  }}
                />
              </label>
            )}

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('raiseOrder.ownershipLabel', { position: index + 1 })}
              </span>
              <select
                className="lm-input"
                value={draft.ownership}
                onChange={(e) => {
                  change(index, { ownership: e.currentTarget.value })
                }}
              >
                {STOCK_OWNERSHIPS.filter(
                  // `INV-003` has three and the service takes two: a frame on loan from a rep is
                  // somebody else's property that was never bought, so no order raises one.
                  (ownership) => ownership !== 'on_loan',
                ).map((ownership) => (
                  <option key={ownership} value={ownership}>
                    {ownershipLabel(t, ownership)}
                  </option>
                ))}
              </select>
            </label>

            <label className="lm-field lm-field--inline">
              <span className="lm-field__label">
                {t('raiseOrder.productIdLabel', { position: index + 1 })}
              </span>
              <input
                className="lm-input"
                autoComplete="off"
                value={draft.productId}
                onChange={(e) => {
                  change(index, { productId: e.currentTarget.value })
                }}
              />
            </label>
            {/* Without it the delivery cannot be posted to stock at all, which is a thing that
                fails a week later at the dock and not here. */}
            <p className="lm-hint">{t('raiseOrder.productIdMeans')}</p>
          </li>
        ))}
      </ul>

      <button
        type="button"
        className="lm-button lm-button--secondary"
        onClick={() => {
          setLines((previous) => [...previous, EMPTY_LINE])
        }}
      >
        {t('raiseOrder.addLine')}
      </button>

      <button type="button" className="lm-button lm-button--primary" onClick={() => void raise()}>
        {t('raiseOrder.save')}
      </button>
      {/* C.5: this writes a draft. Sending it to the supplier is a separate move on the orders
          screen, and saying so here is what stops somebody waiting for a wholesaler who was never
          told. */}
      <p className="lm-hint">{t('raiseOrder.draftMeans')}</p>
    </section>
  )
}
